import { useMemo, useState, type ReactNode } from 'react';
import { Check, CheckCircle2, ChevronDown, ChevronRight, Dices, Loader2, Undo2, VenetianMask } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DateField } from '@/components/ui/date-field';
import { Input } from '@/components/ui/input';
import { LateralityPicker } from '@/components/ui/laterality-picker';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { IMAGE_TYPES, IMAGE_TYPE_LABELS, isLaterality, typeAllowed, type ImageKind, type ImageType } from '../../data/imageTypes';
import { ADVANCED_FIELDS, type MetadataDraft } from '../../features/upload/sidecar';
import { randomDraft } from './demoData';
import { certainKindOf, initialDraft, wireOf, type FileKind } from './details';
import { canConfirm, isLocked, useEnterpriseUpload, type PrefillSource, type UploadRow } from './store';

/**
 * One labeled field. `edited` marks a value that differs from what a dropped
 * file (or the DICOM header) said: an amber border and a one-click revert to
 * the file's value. `error` is what the server would refuse, or refused.
 * A `<label>` unless told otherwise (`as`): a click on its words reaches the
 * first control inside, which a group of buttons must not take as a choice.
 */
export function Field({
  label,
  edited,
  onRevert,
  error,
  className,
  as: Tag = 'label',
  children,
}: {
  label: string;
  edited?: boolean;
  onRevert?: () => void;
  error?: string;
  className?: string;
  as?: 'label' | 'div';
  children: React.ReactNode;
}) {
  return (
    <Tag
      className={cn(
        'flex flex-col gap-1',
        edited && '[&_input]:border-amber-400/70 [&_button]:border-amber-400/70',
        error && '[&_input]:border-destructive/70 [&_button]:border-destructive/70',
        className,
      )}
    >
      <span className="flex h-4 items-center gap-1.5 text-xs font-medium text-muted-foreground">
        {label}
        {edited && onRevert && (
          <button
            type="button"
            title="Back to what the file said"
            className="rounded p-0.5 text-amber-500 hover:bg-accent"
            onClick={(e) => {
              e.preventDefault();
              onRevert();
            }}
          >
            <Undo2 className="h-3.5 w-3.5" />
          </button>
        )}
      </span>
      {children}
      {error && <span className="text-[11px] leading-4 text-destructive">{error}</span>}
    </Tag>
  );
}

/** Small text button living in a section header (Anonymize, Randomize). */
function HeaderAction({ title, onClick, children }: { title: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      title={title}
      className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-medium normal-case tracking-normal text-muted-foreground hover:bg-accent hover:text-foreground"
      onClick={onClick}
    >
      {children}
    </button>
  );
}

function Section({ title, action, children }: { title: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
        {action}
      </div>
      <div className="grid grid-cols-2 gap-2.5">{children}</div>
    </div>
  );
}

export function Note({ tone, children }: { tone: 'warn' | 'muted' | 'ok'; children: React.ReactNode }) {
  return (
    <p
      className={cn(
        'rounded-md px-2.5 py-1.5 text-xs',
        tone === 'warn' && 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
        tone === 'muted' && 'bg-muted text-muted-foreground',
        tone === 'ok' && 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
      )}
    >
      {children}
    </p>
  );
}

const SOURCE_LABEL: Record<PrefillSource, string> = {
  simple: 'metadata file',
  'dicom-json': 'DICOM JSON file',
  fhir: 'FHIR bundle',
  dicom: 'DICOM tags',
};

/** The advanced fields of the case, and of the image: the Advanced toggle opens by itself when one of its part holds something. */
const CASE_ADVANCED = ['patientIdIssuer', 'accessionIssuer', 'studyId'] as const satisfies readonly (keyof MetadataDraft)[];
const IMAGE_ADVANCED = ADVANCED_FIELDS.filter((k) => !(CASE_ADVANCED as readonly string[]).includes(k));

/**
 * Which fields a form draws: `all` (the upload form, one image and its case
 * together), `case` (the study's patient, accession, date, physician and
 * identifiers) or `image` (the image's own facts). The details editor draws
 * the case once and each image apart; the upload form draws them together.
 */
export type MetadataPart = 'all' | 'case' | 'image';

export interface MetadataFieldsProps {
  draft: MetadataDraft;
  /** Which image kind's defaults count as saying nothing (the Advanced badge). */
  kind: FileKind;
  /** What the file certainly is (a scanner's format, the converter's word), or null: the Types it cannot be are greyed. */
  certain?: ImageKind | null;
  /** The one Type the file can be, when its header says (an X-ray is Radiography): every other is greyed. */
  certainType?: ImageType | null;
  set: (patch: Partial<MetadataDraft>) => void;
  /** What is wrong with these fields, said first by the client, else by the server. */
  errorOf: (...keys: (keyof MetadataDraft)[]) => string | undefined;
  /** The upload form's "edited since the file said so" marks, and their revert. */
  edited?: (k: keyof MetadataDraft) => boolean;
  onRevert?: (...keys: (keyof MetadataDraft)[]) => void;
  disabled?: boolean;
  part?: MetadataPart;
  /** More fields at the end of the Study section (the editor's diagnosis). */
  studyExtra?: ReactNode;
}

/** The form's fields, without a store: the upload form and the details editor draw them alike. */
export function MetadataFields({ draft: d, kind, certain = null, certainType = null, set, errorOf, edited: editedOf, onRevert, disabled, part = 'all', studyExtra }: MetadataFieldsProps) {
  const edited = (k: keyof MetadataDraft) => editedOf?.(k) ?? false;
  const revert = (...keys: (keyof MetadataDraft)[]) => onRevert?.(...keys);
  const field = (k: keyof MetadataDraft) => ({ edited: edited(k), onRevert: () => revert(k), error: errorOf(k) });
  const withCase = part !== 'image';
  const withImage = part !== 'case';

  const defaults = initialDraft(kind);
  const advanced: readonly (keyof MetadataDraft)[] = part === 'case' ? CASE_ADVANCED : part === 'image' ? IMAGE_ADVANCED : ADVANCED_FIELDS;
  const advancedFilled = advanced.filter((k) => d[k].trim() !== '' && d[k] !== defaults[k]).length;
  const [advancedOpen, setAdvancedOpen] = useState(() => advancedFilled > 0 || advanced.some((k) => !!errorOf(k)));

  /** Three-part coded entry (code / scheme / meaning) as one field row; `withCode` says what else a new code sets. */
  const codeRow = (label: string, keys: [keyof MetadataDraft, keyof MetadataDraft, keyof MetadataDraft], withCode?: (code: string) => Partial<MetadataDraft>) => (
    <Field label={label} className="col-span-2" edited={keys.some((k) => edited(k))} onRevert={() => revert(...keys)} error={errorOf(...keys)}>
      <div className="grid grid-cols-3 gap-1.5">
        <Input placeholder="code" value={d[keys[0]]} onChange={(e) => set({ [keys[0]]: e.target.value, ...withCode?.(e.target.value) })} />
        <Input placeholder="scheme" value={d[keys[1]]} onChange={(e) => set({ [keys[1]]: e.target.value })} />
        <Input placeholder="meaning" value={d[keys[2]]} onChange={(e) => set({ [keys[2]]: e.target.value })} />
      </div>
    </Field>
  );
  // All seven Types, those the file certainly cannot be greyed (a scanner's
  // slide is no gross photo); a file not known yet may be any.
  const type = (
    <Field label="Type" {...field('imageType')}>
      <Select value={d.imageType || undefined} onValueChange={(v) => set({ imageType: v })}>
        <SelectTrigger>
          <SelectValue placeholder="Select" />
        </SelectTrigger>
        <SelectContent>
          {IMAGE_TYPES.map((t) => (
            <SelectItem key={t} value={t} disabled={!typeAllowed(t, certain, certainType)}>
              {IMAGE_TYPE_LABELS[t]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  );

  return (
    <>
      <fieldset disabled={disabled} className="contents">
        {withCase && (
          <Section
            title="Patient"
            action={
              <div className="flex items-center gap-0.5">
                <HeaderAction
                  title="Replace patient identity with anonymous values"
                  onClick={() =>
                    // DICOM de-id practice (PS3.15): the issuer of a replaced MRN
                    // and the referring physician identify the care episode too.
                    set({
                      patientFamily: 'ANONYMIZED',
                      patientGiven: '',
                      patientId: `ANON-${crypto.randomUUID().slice(0, 8).toUpperCase()}`,
                      patientBirthDate: '',
                      patientSex: '',
                      patientIdIssuer: '',
                      referringPhysician: '',
                    })
                  }
                >
                  <VenetianMask className="h-3.5 w-3.5" />
                  Anonymize
                </HeaderAction>
                <HeaderAction title="Replace patient identity with a made-up one (demo data)" onClick={() => set(randomDraft())}>
                  <Dices className="h-3.5 w-3.5" />
                  Randomize
                </HeaderAction>
              </div>
            }
          >
            <Field label="Last name" {...field('patientFamily')}>
              <Input value={d.patientFamily} onChange={(e) => set({ patientFamily: e.target.value })} />
            </Field>
            <Field label="First name" {...field('patientGiven')}>
              <Input value={d.patientGiven} onChange={(e) => set({ patientGiven: e.target.value })} />
            </Field>
            <Field label="Patient ID (MRN)" {...field('patientId')}>
              <Input value={d.patientId} onChange={(e) => set({ patientId: e.target.value })} />
            </Field>
            <Field label="Birth date" {...field('patientBirthDate')}>
              <DateField value={d.patientBirthDate} onChange={(v) => set({ patientBirthDate: v })} placeholder="Select date" />
            </Field>
            <Field label="Sex" {...field('patientSex')}>
              <Select value={d.patientSex || undefined} onValueChange={(v) => set({ patientSex: v as MetadataDraft['patientSex'] })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="F">Female</SelectItem>
                  <SelectItem value="M">Male</SelectItem>
                  <SelectItem value="O">Other</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Age" {...field('patientAge')}>
              <Input value={d.patientAge} onChange={(e) => set({ patientAge: e.target.value })} placeholder="54, 54Y, 8M or 90+" />
            </Field>
          </Section>
        )}

        {withCase && (
          <Section title="Study">
            <Field label="Accession #" {...field('accession')}>
              <Input value={d.accession} onChange={(e) => set({ accession: e.target.value })} placeholder="groups images into one case" />
            </Field>
            <Field label="Study date & time" {...field('studyDateTime')}>
              <DateField value={d.studyDateTime} onChange={(v) => set({ studyDateTime: v })} withTime placeholder="Select date & time" />
            </Field>
            <Field label="Description" {...field('studyDescription')} className="col-span-2">
              <Input value={d.studyDescription} onChange={(e) => set({ studyDescription: e.target.value })} />
            </Field>
            <Field label="Referring physician (Last, First)" {...field('referringPhysician')}>
              <Input value={d.referringPhysician} onChange={(e) => set({ referringPhysician: e.target.value })} />
            </Field>
            {studyExtra}
          </Section>
        )}

        {/* Its Name (the Series Description) is what the viewer's image cards show: first. */}
        {withImage && (
          <Section title="Image">
            <Field label="Name" {...field('seriesDescription')} className="col-span-2">
              <Input value={d.seriesDescription} maxLength={64} onChange={(e) => set({ seriesDescription: e.target.value })} />
            </Field>
            {type}
          </Section>
        )}

        {withImage && (
          <Section title="Specimen">
            <Field label="Container ID" {...field('containerId')}>
              <Input value={d.containerId} onChange={(e) => set({ containerId: e.target.value })} />
            </Field>
            <Field label="Specimen ID" {...field('specimenId')}>
              <Input value={d.specimenId} onChange={(e) => set({ specimenId: e.target.value })} />
            </Field>
          </Section>
        )}
      </fieldset>

      <button
        type="button"
        onClick={() => setAdvancedOpen((o) => !o)}
        className="flex items-center gap-1 self-start text-xs font-medium text-muted-foreground hover:text-foreground"
      >
        {advancedOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        Advanced fields
        {advancedFilled > 0 && <span className="rounded-full bg-primary/15 px-1.5 text-[10px] text-primary">{advancedFilled}</span>}
      </button>

      {advancedOpen && (
        <fieldset disabled={disabled} className="contents">
          {withCase && (
            <Section title="Identifiers">
              <Field label="Patient ID issuer" {...field('patientIdIssuer')}>
                <Input value={d.patientIdIssuer} onChange={(e) => set({ patientIdIssuer: e.target.value })} />
              </Field>
              <Field label="Accession issuer" {...field('accessionIssuer')}>
                <Input value={d.accessionIssuer} onChange={(e) => set({ accessionIssuer: e.target.value })} />
              </Field>
              <Field label="Study ID" {...field('studyId')}>
                <Input value={d.studyId} onChange={(e) => set({ studyId: e.target.value })} />
              </Field>
            </Section>
          )}

          {withImage && (
            <>
              <Section title="Equipment">
                <Field label="Manufacturer" {...field('manufacturer')}>
                  <Input value={d.manufacturer} onChange={(e) => set({ manufacturer: e.target.value })} />
                </Field>
                <Field label="Station name" {...field('stationName')}>
                  <Input value={d.stationName} onChange={(e) => set({ stationName: e.target.value })} />
                </Field>
                <Field label="Device serial" {...field('deviceSerial')}>
                  <Input value={d.deviceSerial} onChange={(e) => set({ deviceSerial: e.target.value })} />
                </Field>
              </Section>

              <Section title="Specimen detail">
                <Field label="Container ID issuer" {...field('containerIdIssuer')}>
                  <Input value={d.containerIdIssuer} onChange={(e) => set({ containerIdIssuer: e.target.value })} />
                </Field>
                <Field label="Specimen description" {...field('specimenDescription')} className="col-span-2">
                  <Input value={d.specimenDescription} onChange={(e) => set({ specimenDescription: e.target.value })} />
                </Field>
                {codeRow('Specimen type (code / scheme / meaning)', ['specimenTypeValue', 'specimenTypeScheme', 'specimenTypeMeaning'])}
                {codeRow('Anatomic site (code / scheme / meaning)', ['anatomicSiteValue', 'anatomicSiteScheme', 'anatomicSiteMeaning'])}
                <Field label="Laterality" as="div" {...field('laterality')}>
                  <LateralityPicker value={isLaterality(d.laterality) ? d.laterality : null} disabled={disabled} onChange={(v) => set({ laterality: v ?? '' })} />
                </Field>
              </Section>

              <Section title="Requested procedure">
                {codeRow('Procedure (code / scheme / meaning)', ['procedureValue', 'procedureScheme', 'procedureMeaning'])}
              </Section>
            </>
          )}
        </fieldset>
      )}
    </>
  );
}

/**
 * The details of one image. Saved as they are typed (only what changed), and
 * confirmed with the button at the bottom: the image is imported — shown in the
 * worklist — once it is processed and its details are confirmed, by this
 * button or by the LIS answering.
 */
export function MetadataForm({ row }: { row: UploadRow }) {
  const setFields = useEnterpriseUpload((s) => s.setFields);
  const confirm = useEnterpriseUpload((s) => s.confirm);

  const d = row.draft;
  const locked = isLocked(row);
  const set = (patch: Partial<MetadataDraft>) => setFields(row.id, patch);
  const problems = useMemo(() => wireOf(d).problems, [d]);

  /** The first thing wrong with a field: said before it is sent, or the server's refusal. */
  const errorOf = (...keys: (keyof MetadataDraft)[]) =>
    problems.find((p) => keys.includes(p.field))?.message ??
    (row.detailsError?.field && keys.includes(row.detailsError.field) ? row.detailsError.message : undefined);
  const edited = (k: keyof MetadataDraft) => !!row.prefill && d[k] !== row.prefill[k];
  const revert = (...keys: (keyof MetadataDraft)[]) => {
    if (!row.prefill) return;
    const patch: Partial<MetadataDraft> = {};
    for (const k of keys) (patch as Record<string, string>)[k] = row.prefill[k];
    set(patch);
  };

  const generalError = row.detailsError && !row.detailsError.field ? row.detailsError.message : null;

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <div className="flex items-baseline justify-between gap-2">
        <div className="truncate text-sm font-semibold" title={row.name}>
          {row.name}
        </div>
        {row.prefillSource && <span className="shrink-0 text-xs text-muted-foreground">from {SOURCE_LABEL[row.prefillSource]}</span>}
      </div>

      {row.stage === 'published' ? (
        <Note tone="ok">Imported. Its details can no longer be changed here.</Note>
      ) : row.stage === 'failed' ? (
        <Note tone="muted">This image could not be imported{row.stageError ? `: ${row.stageError}` : '.'}</Note>
      ) : row.ready ? (
        <Note tone="muted">Details confirmed. The image is imported as soon as its import finishes.</Note>
      ) : row.metadata ? (
        <Note tone="warn">
          Waiting for this image’s metadata from the LIS{row.metadata.overdue ? ' (overdue)' : ''}. Confirm to import it with the details below instead.
        </Note>
      ) : row.stage === 'needs-details' ? (
        <Note tone="warn">Review the details and confirm to import it.</Note>
      ) : null}
      {row.partial && !locked && (
        <Note tone="muted">
          The server could not send all of this image’s details: a blank field may already hold a value there, and what you enter replaces it.
        </Note>
      )}

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto border-t pr-1 pt-3">
        <MetadataFields draft={d} kind={row.kind} certain={certainKindOf(row)} certainType={row.certainType ?? null} set={set} errorOf={errorOf} edited={edited} onRevert={revert} disabled={locked || row.confirming} />
      </div>

      <div className="flex flex-col gap-1.5 border-t pt-3">
        <div className="flex items-center gap-3">
          {row.stage === 'published' ? (
            <Button size="sm" disabled>
              <Check />
              Imported
            </Button>
          ) : row.ready ? (
            <Button size="sm" disabled title="The image is imported as soon as its import finishes">
              <CheckCircle2 />
              Confirmed
            </Button>
          ) : (
            <Button
              size="sm"
              disabled={!canConfirm(row) || row.confirming || problems.length > 0}
              onClick={() => void confirm(row.id)}
              title={problems.length ? problems[0].message : 'Confirm these details: the image is imported once they are confirmed and its import finishes'}
            >
              {row.confirming ? <Loader2 className="animate-spin" /> : <CheckCircle2 />}
              Confirm details
            </Button>
          )}
          <span className="text-xs text-muted-foreground">
            {locked ? null : row.dirty && !row.detailsError ? (row.imageId ? 'Saving…' : 'Saved once the upload starts') : !row.dirty && row.imageId ? 'Saved' : null}
          </span>
        </div>
        {row.confirmError && <p className="text-xs text-destructive">{row.confirmError}</p>}
        {generalError && <p className="text-xs text-destructive">Not saved: {generalError}</p>}
      </div>
    </div>
  );
}
